// src/features/tenant/SuspendTenantDialog.tsx
// Suspend a tenant — small form-kit dialog for TenantDangerZonePage
// (docs/ux/14-forms.md). Destructive-but-reversible action: captures a
// reason and an optional detail/condition, not a full FormDialog since it's
// only two fields.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, FormRow, AutoGrowTextarea } from '@/components/form';
import { Select } from '@/components/Select';

const SUSPENSION_REASONS = [
  'BILLING_FAILURE',
  'POLICY_VIOLATION',
  'SECURITY_INCIDENT',
  'ABUSE',
  'LEGAL',
  'OWNER_REQUEST',
  'INACTIVITY',
  'QUOTA_EXCEEDED',
] as const;

export type SuspensionReason = (typeof SUSPENSION_REASONS)[number];

/** Map the short local reason key to the wire enum value (TenantSuspensionReason). */
export function toSuspensionReasonEnum(reason: SuspensionReason): string {
  return `TENANT_SUSPENSION_REASON_${reason}`;
}

function reasonLabelKey(reason: string): string {
  switch (reason) {
    case 'BILLING_FAILURE':
      return 'tenant.danger.reasonBilling';
    case 'POLICY_VIOLATION':
      return 'tenant.danger.reasonPolicy';
    case 'SECURITY_INCIDENT':
      return 'tenant.danger.reasonSecurity';
    case 'ABUSE':
      return 'tenant.danger.reasonAbuse';
    case 'LEGAL':
      return 'tenant.danger.reasonLegal';
    case 'OWNER_REQUEST':
      return 'tenant.danger.reasonOwner';
    case 'INACTIVITY':
      return 'tenant.danger.reasonInactivity';
    case 'QUOTA_EXCEEDED':
      return 'tenant.danger.reasonQuota';
    default:
      return reason;
  }
}

export interface SuspendTenantDialogProps {
  open: boolean;
  tenantName: string;
  onClose: () => void;
  onSubmit: (reason: SuspensionReason, detail: string) => Promise<void>;
}

export function SuspendTenantDialog({ open, tenantName, onClose, onSubmit }: SuspendTenantDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState<SuspensionReason>('BILLING_FAILURE');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setReason('BILLING_FAILURE');
    setDetail('');
    setError(null);
  }, [open]);

  const dirty = !!detail.trim();

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(reason, detail.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('tenant.danger.suspendButton')}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('tenant.danger.suspendTitle')}
    >
      <div className="space-y-3 px-3.5 py-3">
        <p className="text-sm text-muted-foreground">
          {t('tenant.danger.suspendConfirm', { name: tenantName })}
        </p>
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <FormRow label={t('tenant.danger.reasonLabel')} labelWidth="sm">
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value as SuspensionReason)}
            options={SUSPENSION_REASONS.map((r) => ({ value: r, label: t(reasonLabelKey(r)) }))}
            fullWidth={false}
            className="max-w-xs"
            aria-label={t('tenant.danger.reasonLabel')}
          />
        </FormRow>
        <FormRow label={t('tenant.danger.detailLabel')} labelWidth="sm">
          <AutoGrowTextarea
            value={detail}
            onChange={setDetail}
            placeholder={t('tenant.danger.detailPlaceholder')}
            minRows={2}
          />
        </FormRow>
      </div>
    </FormDialog>
  );
}

export default SuspendTenantDialog;
